import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, TextInput, KeyboardAvoidingView, Platform,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';

const GUESS_SECONDS = 30;

export default function GameImpostorGuessScreen() {
  const navigation = useNavigation();
  const toast = useToast();
  const {
    room, currentRound, amImpostor, impostorGuess,
    resolveLocalRound, localPlayers,
  } = useGame();

  const [guess, setGuess] = useState('');
  const [loading, setLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(GUESS_SECONDS);
  const timerRef = useRef(null);
  const hasNavigated = useRef(false);

  const isLocal = room?.mode === 'LOCAL';
  const isImpostorTurn = isLocal ? true : amImpostor;

  // Redirect when round finishes (online) or room disappears
  useEffect(() => {
    if (!room || !currentRound) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    } else if (currentRound.status === 'FINISHED' && !hasNavigated.current) {
      hasNavigated.current = true;
      navigation.navigate(SCREEN_NAMES.GAME_RESULT);
    }
  }, [room?.id, currentRound?.status]);

  // Countdown timer
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleTimerExpiry();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, []);

  if (!room || !currentRound) return null;

  const impostorName = isLocal
    ? (localPlayers[currentRound.impostorId]?.name ?? "L'Imposteur")
    : room.players?.find((p) => p.userId === currentRound.impostorId)?.user?.firstName ?? "L'Imposteur";

  const handleTimerExpiry = async () => {
    if (hasNavigated.current) return;
    hasNavigated.current = true;
    if (isLocal) {
      resolveLocalRound('VILLAGERS_WIN');
      navigation.navigate(SCREEN_NAMES.GAME_RESULT);
    } else if (amImpostor) {
      try {
        await impostorGuess('__timeout__');
        navigation.navigate(SCREEN_NAMES.GAME_RESULT);
      } catch {}
    }
  };

  const handleSubmit = async () => {
    if (!guess.trim() || hasNavigated.current) return;
    clearInterval(timerRef.current);
    hasNavigated.current = true;
    try {
      setLoading(true);
      if (isLocal) {
        const correct = guess.trim().toLowerCase() === currentRound.secretWord.toLowerCase();
        resolveLocalRound(correct ? 'IMPOSTOR_WIN_GUESSED' : 'VILLAGERS_WIN');
        navigation.navigate(SCREEN_NAMES.GAME_RESULT);
      } else {
        await impostorGuess(guess.trim());
        navigation.navigate(SCREEN_NAMES.GAME_RESULT);
      }
    } catch (e) {
      hasNavigated.current = false;
      toast.show(e.message || 'Erreur', { type: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  const mins = Math.floor(timeLeft / 60).toString().padStart(2, '0');
  const secs = (timeLeft % 60).toString().padStart(2, '0');
  const timerLabel = `${mins}:${secs}`;

  if (!isImpostorTurn) {
    return (
      <View style={styles.suspenseContainer}>
        <Text style={styles.suspenseIcon}>⏳</Text>
        <Text style={styles.suspenseTitle}>
          {impostorName}
          {' '}
          devine le mot…
        </Text>
        <Text style={styles.suspenseTimer}>
          {timerLabel}
        </Text>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.flex}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <View style={styles.container}>
        <Text style={styles.title}>
          Tu es l&apos;Imposteur !
        </Text>
        <Text style={styles.sub}>
          Devine le mot secret pour gagner
        </Text>
        <Text style={[styles.timer, timeLeft <= 10 && styles.timerUrgent]}>
          {timerLabel}
        </Text>

        <TextInput
          style={styles.input}
          placeholder="Quel est le mot secret ?"
          value={guess}
          onChangeText={setGuess}
          autoCapitalize="none"
          returnKeyType="send"
          onSubmitEditing={handleSubmit}
        />

        <TouchableOpacity
          style={[styles.guessBtn, (!guess.trim() || loading) && styles.btnDisabled]}
          onPress={handleSubmit}
          disabled={!guess.trim() || loading}
        >
          <Text style={styles.guessBtnText}>
            {loading ? 'Envoi…' : 'Soumettre ma réponse'}
          </Text>
        </TouchableOpacity>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: { flex: 1, backgroundColor: '#1a1a2e' },
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  title: {
    fontSize: 30,
    fontWeight: '900',
    color: '#fff',
    marginBottom: 8,
    textAlign: 'center',
  },
  sub: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 16,
    marginBottom: 20,
    textAlign: 'center',
  },
  timer: {
    fontSize: 48,
    fontWeight: '900',
    color: '#fff',
    marginBottom: 32,
  },
  timerUrgent: { color: '#ff6b6b' },
  input: {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderRadius: 14,
    padding: 18,
    fontSize: 20,
    color: '#fff',
    textAlign: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.2)',
  },
  guessBtn: {
    backgroundColor: '#fff',
    borderRadius: 14,
    paddingVertical: 16,
    paddingHorizontal: 40,
    alignItems: 'center',
  },
  btnDisabled: { opacity: 0.4 },
  guessBtnText: { color: '#1a1a2e', fontWeight: '700', fontSize: 17 },
  suspenseContainer: {
    flex: 1,
    backgroundColor: '#0f3460',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
  },
  suspenseIcon: { fontSize: 72 },
  suspenseTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#fff',
    textAlign: 'center',
  },
  suspenseTimer: { fontSize: 56, fontWeight: '900', color: '#fff' },
});
